/**
 * Shared error model for outbound HTTP failures.
 *
 * Classifies raw HTTP client errors into a small set of consistent failure
 * categories so that dependency failures, latency issues, and invalid
 * responses can be diagnosed the same way across every integration.
 */

type HttpFailureCategory =
	| 'transport' // request never received a response (DNS/connection reset/etc.)
	| 'timeout' // request aborted due to exceeding the configured timeout
	| 'http_error' // a response was received with a non-2xx status code
	| 'validation' // a 2xx response was received but failed shape/schema validation

/**
 * Structured telemetry attached to outbound HTTP errors so callers and the
 * shared client can reason about a failure without having to re-derive it
 * from the raw HTTP error each time. This is the single source of truth for
 * this shape — do not redeclare an equivalent interface elsewhere (e.g. in
 * ./index.ts); import and reuse this one so the two can't drift apart.
 */
export interface HttpTelemetry {
	category: HttpFailureCategory
	targetSystem: string
	method: string
	status?: number
	durationMs: number
	redactedUrl: string
}

const TIMEOUT_CODES = new Set(['ECONNABORTED', 'ETIMEDOUT'])

/**
 * Minimal shape of an outbound HTTP error needed for classification. Kept
 * duck-typed (rather than importing a specific HTTP library's error type) so
 * this module has no dependency on any particular HTTP client implementation.
 */
export interface ClassifiableHttpError {
	code?: string
	message?: string
	response?: {status: number}
	category?: HttpFailureCategory
}

/**
 * Determines the failure category for a raw outbound HTTP error.
 *
 * - An explicit `category` was provided by the caller/thrower: use it as-is.
 * - No `response` and a timeout-flavoured code/message: `timeout`
 * - No `response` at all: `transport` (DNS failure, connection reset, etc.)
 */
export function classifyError(
	error: ClassifiableHttpError
): HttpFailureCategory {
	if (error.category) {
		return error.category
	}

	if (error.response) {
		return 'http_error'
	}

	const isTimeout =
		(error.code && TIMEOUT_CODES.has(error.code)) ||
		/timeout/i.test(error.message || '')

	if (isTimeout) {
		return 'timeout'
	}

	return 'transport'
}

/**
 * Error thrown by callers that received a successful HTTP response but the
 * response body failed validation (e.g. zod parsing). Kept separate from
 * transport/timeout/http_error since the request itself succeeded.
 */
export class HttpValidationError extends Error {
	readonly category: HttpFailureCategory = 'validation'
	readonly cause?: unknown

	constructor(message: string, cause?: unknown) {
		super(message)
		this.name = 'HttpValidationError'
		this.cause = cause
	}
}

/**
 * Derives a stable "target system" label from a URL for grouping telemetry
 * (e.g. `disease-api.elancoapps.com`). Returns 'unknown' if the URL is
 * empty/undefined or is an absolute URL that fails to parse (target system
 * can't be derived), and 'internal' for relative URLs (e.g. `/api/foo`),
 * which are expected not to parse as absolute `URL`s.
 */
export function getTargetSystem(url: string | undefined): string {
	if (!url) return 'unknown'
	try {
		return new URL(url).hostname
	} catch {
		if (/^[a-z][a-z0-9+.-]*:/i.test(url)) {
			return 'unknown'
		}
		return 'internal'
	}
}

export function getSafeErrorType(error: unknown): string {
	if (error instanceof Error) {
		return error.name || 'Error'
	}
	return 'UnknownError'
}
